import { useMemo } from 'react';

import type {
  FollowupHistoryItemSchema,
  TransmissionStrategyConfig,
  TreatmentRecordRequestSchema,
} from '@/services/api/generated/treatmentRecordRequestsService';
import { treatmentRecordRequestService } from '@/services/api/treatmentRecordRequestService';

interface UseFollowUpTransmissionParams {
  matterId: string;
  providerId: string;
  request: TreatmentRecordRequestSchema | undefined;
}

export interface TransmissionHistoryEntry extends FollowupHistoryItemSchema {
  isInitial: boolean;
  channelIcon: 'fax' | 'email' | 'mail' | 'unknown';
}

export interface FollowUpTransmissionState {
  // Data
  history: TransmissionHistoryEntry[];
  strategy: TransmissionStrategyConfig | null;

  // Computed
  totalSent: number;
  maxAttempts: number;
  isAutoEnabled: boolean;
  isComplete: boolean;
  nextScheduledDate: Date | null;
  lastTransmissionDate: Date | null;

  // Loading states
  isHistoryLoading: boolean;
  isStrategyLoading: boolean;
  isLoading: boolean;

  // Errors
  historyError: unknown;
  strategyError: unknown;

  // Refetch functions
  refetchHistory: () => void;
  refetchStrategy: () => void;
}

const SUBMITTED_STATUSES = [
  'request_submitted',
  'request_received',
  'awaiting_invoice',
  'pending_payment',
  'records_pending',
  'records_delivered',
  'records_ingested',
  'rejected',
  'cancelled',
];

const getChannelIcon = (
  channel: string | null | undefined,
): TransmissionHistoryEntry['channelIcon'] => {
  if (!channel) return 'unknown';
  const normalized = channel.toLowerCase();
  if (normalized.includes('fax')) return 'fax';
  if (normalized.includes('email')) return 'email';
  if (normalized.includes('mail') || normalized.includes('letter'))
    return 'mail';
  return 'unknown';
};

export const useFollowUpTransmission = ({
  matterId,
  providerId,
  request,
}: UseFollowUpTransmissionParams): FollowUpTransmissionState => {
  const hasSubmittedRequest =
    !!request?.status && SUBMITTED_STATUSES.includes(request.status);

  const shouldSkip = !request || !hasSubmittedRequest;

  const {
    data: historyData,
    isLoading: isHistoryLoading,
    error: historyError,
    refetch: refetchHistory,
  } = treatmentRecordRequestService.useGetFollowupHistoryQuery(
    {
      matterId,
      providerId,
      requestId: request?.id ?? '',
    },
    {
      skip: shouldSkip,
    },
  );

  const {
    data: strategyData,
    isLoading: isStrategyLoading,
    error: strategyError,
    refetch: refetchStrategy,
  } = treatmentRecordRequestService.useTransmissionStrategyQuery(
    {
      matterId,
      providerId,
      requestId: request?.id ?? '',
    },
    {
      skip: shouldSkip,
    },
  );

  const history = useMemo<TransmissionHistoryEntry[]>(() => {
    if (!historyData?.followups) return [];

    return historyData.followups
      .map((item, index, arr) => ({
        ...item,
        isInitial: index === arr.length - 1, // Last item (oldest) is initial
        channelIcon: getChannelIcon(item.channel),
      }))
      .sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      );
  }, [historyData?.followups]);

  const strategy = strategyData ?? null;

  const totalSent = history.length;
  const maxAttempts = strategy?.max_attempts ?? 3;
  const isAutoEnabled = strategy?.enabled ?? false;
  const isComplete = totalSent >= maxAttempts;

  const nextScheduledDate = useMemo<Date | null>(() => {
    if (!isAutoEnabled || isComplete || history.length === 0) return null;

    const intervalDays = strategy?.interval_days ?? 3;
    const lastEntry = history[0]; // Most recent (sorted desc)
    if (!lastEntry) return null;

    const lastDate = new Date(lastEntry.transmitted_at || lastEntry.created_at);
    const nextDate = new Date(lastDate);
    nextDate.setDate(nextDate.getDate() + intervalDays);

    return nextDate;
  }, [isAutoEnabled, isComplete, history, strategy?.interval_days]);

  const lastTransmissionDate = useMemo<Date | null>(() => {
    if (history.length === 0) return null;
    const lastEntry = history[0];
    return new Date(lastEntry.transmitted_at || lastEntry.created_at);
  }, [history]);

  return {
    history,
    strategy,
    totalSent,
    maxAttempts,
    isAutoEnabled,
    isComplete,
    nextScheduledDate,
    lastTransmissionDate,
    isHistoryLoading,
    isStrategyLoading,
    isLoading: isHistoryLoading || isStrategyLoading,
    historyError,
    strategyError,
    refetchHistory,
    refetchStrategy,
  };
};
